import { useEffect } from 'react';

const SITE = 'https://soal.help';
export { SITE };

/**
 * Lightweight per-page SEO for our SPA: sets document.title, meta description,
 * canonical link, Open Graph, Twitter Card, keywords, and optional JSON-LD
 * structured data. Google + LLM crawlers (GPTBot / ClaudeBot / Google-Extended)
 * execute JS and read these, so public pages get proper titles/snippets/rich
 * results in search AND get quoted correctly in AI Overview answers.
 */
export function useSeo({ title, description, path, keywords, ogImage, jsonLd } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const ensureMeta = (attr, name) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      return el;
    };

    if (description) {
      ensureMeta('name', 'description').setAttribute('content', description);
      ensureMeta('property', 'og:description').setAttribute('content', description);
      ensureMeta('name', 'twitter:description').setAttribute('content', description);
    }
    if (title) {
      ensureMeta('property', 'og:title').setAttribute('content', title);
      ensureMeta('name', 'twitter:title').setAttribute('content', title);
    }
    if (keywords) {
      const kw = Array.isArray(keywords) ? keywords.join(', ') : keywords;
      ensureMeta('name', 'keywords').setAttribute('content', kw);
    }
    if (ogImage) {
      ensureMeta('property', 'og:image').setAttribute('content', ogImage);
      ensureMeta('name', 'twitter:image').setAttribute('content', ogImage);
    }

    if (path) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', SITE + path);
      ensureMeta('property', 'og:url').setAttribute('content', SITE + path);
    }

    return () => { document.title = prevTitle; };
  }, [title, description, path, keywords, ogImage]);

  // JSON-LD structured data (Organization / FAQPage / BlogPosting / HowTo / ...)
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : '';
  useEffect(() => {
    if (!jsonLdStr) return undefined;
    const data = JSON.parse(jsonLdStr);
    const items = (Array.isArray(data) ? data : [data])
      .map((d) => ({ '@context': 'https://schema.org', ...d }));
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(items.length === 1 ? items[0] : items);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [jsonLdStr]);
}
